import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import clsx from 'clsx';
import { useDocument, useToggle } from '../hooks/index.js';
import { useUniqueId } from '../utils/unique-id.js';
import { FileDropZoneContext } from './FileDropZoneContext.js';
import { FileDropZoneInput } from './FileDropZoneInput.js';
import { FileDropZoneLabel } from './FileDropZoneLabel.js';
import { fileAccepted, getDataTransferFiles } from './utils.js';
export const FileDropZone = ({ dropOnPage, children, disabled = false, outline = true, accept, active, overlay = true, allowMultiple = true, overlayText, errorOverlayText, id: idProp, type = 'file', onClick, invalid = false, openFileDialog, onFileDialogClose, customValidator, onDrop, onDropAccepted, onDropRejected, onDragEnter, onDragOver, onDragLeave, className, }) => {
    const { document } = useDocument();
    const node = useRef(null);
    const dragTargets = useRef([]);
    const [dragging, setDragging] = useState(false);
    const [internalError, setInternalError] = useState(false);
    const { value: focused, setTrue: handleFocus, setFalse: handleBlur, } = useToggle(false);
    const getValidatedFiles = useCallback((files) => {
        const acceptedFiles = [];
        const rejectedFiles = [];
        Array.from(files).forEach((file) => {
            if (!fileAccepted(file, accept) ||
                (customValidator && !customValidator(file))) {
                rejectedFiles.push(file);
            }
            else {
                acceptedFiles.push(file);
            }
        });
        if (!allowMultiple) {
            acceptedFiles.splice(1, acceptedFiles.length);
            rejectedFiles.push(...acceptedFiles.slice(1));
        }
        return { files, acceptedFiles, rejectedFiles };
    }, [accept, allowMultiple, customValidator]);
    const handleDrop = useCallback((event) => {
        stopEvent(event);
        if (disabled) {
            return;
        }
        const fileList = getDataTransferFiles(event);
        const { files, acceptedFiles, rejectedFiles } = getValidatedFiles(fileList);
        dragTargets.current = [];
        setDragging(false);
        setInternalError(rejectedFiles.length > 0);
        onDrop === null || onDrop === void 0 ? void 0 : onDrop(files, acceptedFiles, rejectedFiles);
        if (onDropAccepted && acceptedFiles.length) {
            onDropAccepted === null || onDropAccepted === void 0 ? void 0 : onDropAccepted(acceptedFiles);
        }
        if (onDropRejected && rejectedFiles.length) {
            onDropRejected(rejectedFiles);
        }
        ;
        event.target.value = '';
    }, [disabled, getValidatedFiles, onDrop, onDropAccepted, onDropRejected]);
    const handleDragEnter = useCallback((event) => {
        stopEvent(event);
        if (disabled) {
            return;
        }
        const fileList = getDataTransferFiles(event);
        if (event.target && !dragTargets.current.includes(event.target)) {
            dragTargets.current.push(event.target);
        }
        if (dragging) {
            return;
        }
        const { rejectedFiles } = getValidatedFiles(fileList);
        setDragging(true);
        setInternalError(rejectedFiles.length > 0);
        onDragEnter === null || onDragEnter === void 0 ? void 0 : onDragEnter();
    }, [disabled, dragging, getValidatedFiles, onDragEnter]);
    const handleDragOver = useCallback((event) => {
        stopEvent(event);
        if (disabled) {
            return;
        }
        onDragOver === null || onDragOver === void 0 ? void 0 : onDragOver();
    }, [disabled, onDragOver]);
    const handleDragLeave = useCallback((event) => {
        event.preventDefault();
        if (disabled) {
            return;
        }
        const isServer = typeof window === 'undefined';
        dragTargets.current = dragTargets.current.filter((el) => {
            const compareNode = dropOnPage && !isServer ? document : node.current;
            return el !== event.target && compareNode && compareNode.contains(el);
        });
        if (dragTargets.current.length > 0) {
            return;
        }
        setDragging(false);
        setInternalError(false);
        onDragLeave === null || onDragLeave === void 0 ? void 0 : onDragLeave();
    }, [dropOnPage, disabled, onDragLeave]);
    useEffect(() => {
        const dropNode = dropOnPage ? document : node.current;
        if (!dropNode) {
            return;
        }
        dropNode.addEventListener('drop', handleDrop);
        dropNode.addEventListener('dragover', handleDragOver);
        dropNode.addEventListener('dragenter', handleDragEnter);
        dropNode.addEventListener('dragleave', handleDragLeave);
        return () => {
            dropNode.removeEventListener('drop', handleDrop);
            dropNode.removeEventListener('dragover', handleDragOver);
            dropNode.removeEventListener('dragenter', handleDragEnter);
            dropNode.removeEventListener('dragleave', handleDragLeave);
        };
    }, [dropOnPage, handleDrop, handleDragOver, handleDragEnter, handleDragLeave]);
    const id = useUniqueId('DropZone', idProp);
    const typeLabel = `${type}${allowMultiple ? 's' : ''}`;
    const overlayTextWithDefault = overlayText === undefined ? `Drop ${typeLabel} to upload` : overlayText;
    const errorOverlayTextWithDefault = errorOverlayText === undefined
        ? `${type.toUpperCase()} type is not valid`
        : errorOverlayText;
    const inputAttributes = {
        id,
        accept,
        disabled,
        type: 'file',
        multiple: allowMultiple,
        onChange: handleDrop,
        onFocus: handleFocus,
        onBlur: handleBlur,
    };
    const classes = clsx('relative flex justify-center bg-surface rounded-md min-h-[60px]', 'border-2 border-dashed', outline && 'p-1', focused && 'focus-within:ring', (active || dragging) && 'bg-surface-hovered border-line', disabled
        ? 'opacity-60 cursor-default pointer-events-none'
        : 'cursor-pointer', internalError || invalid
        ? 'border-line-negative'
        : 'border-line-subdued hover:border-line', className);
    const dragOverlay = (active || dragging) &&
        !internalError &&
        !invalid &&
        overlay &&
        overlayMarkup('interactive', overlayTextWithDefault);
    const dragErrorOverlay = dragging &&
        (internalError || invalid) &&
        overlayMarkup('critical', errorOverlayTextWithDefault);
    const context = useMemo(() => ({
        disabled,
        focused,
        type: type || 'file',
        allowMultiple,
    }), [disabled, focused, type, allowMultiple]);
    return (React.createElement(FileDropZoneContext.Provider, { value: context },
        React.createElement("div", { ref: node, className: classes, "aria-disabled": disabled, onClick: handleClick, onDragStart: stopEvent },
            dragOverlay,
            dragErrorOverlay,
            React.createElement("div", { className: "sr-only" },
                React.createElement(FileDropZoneInput, Object.assign({}, inputAttributes, { openFileDialog: openFileDialog, onFileDialogClose: onFileDialogClose }))),
            React.createElement("div", { className: "flex-1 min-w-0" }, children))));
    function overlayMarkup(color, text) {
        return (React.createElement("div", { className: clsx('absolute inset-0', 'flex items-center justify-center', 'bg-surface rounded-md pointer-events-none') },
            React.createElement("div", { className: "text-sm first-letter:capitalize text-content-subdued" }, text)));
    }
    function open() {
        const fileInputNode = node.current && node.current.querySelector(`#${id}`);
        if (fileInputNode && fileInputNode instanceof HTMLElement) {
            fileInputNode.click();
        }
    }
    function handleClick(event) {
        if (disabled) {
            return;
        }
        return onClick ? onClick(event) : open();
    }
};
function stopEvent(event) {
    event.preventDefault();
    event.stopPropagation();
}
FileDropZone.Label = FileDropZoneLabel;
//# sourceMappingURL=FileDropZone.js.map